import { useSession } from '@supabase/auth-helpers-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import supabase from '../config/supabaseClient';
import EventViewer from './eventviewer';
import '../components/speakeasy.css';
import CloseIcon from '../icons/closeicon';
import SendInviteIcon from '../icons/sendinviteicon';
import MeltingFaceIcon from '../icons/meltingfaceicon';


export default function Speakeasy() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('loading'); // 'loading' | 'enter-passphrase' | 'verified' | 'error'
  const [event, setEvent] = useState(null);
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState('');
  const vrc = params.get('vrc')?.trim().toUpperCase();
  const [contactEmail, setContactEmail] = useState('');
  const [contactFeedback, setContactFeedback] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSocials, setContactSocials] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [errors, setErrors] = useState({ name: false, email: false, message: false });
  const [showContactModal, setShowContactModal] = useState(false);
  const session = useSession();
  const user = session?.user || null;


  useEffect(() => {
    const validateVRC = async () => {
      if (!vrc) {
        setStep('error');
        return;
      }

      console.log('🔍 VRC param from URL:', vrc);
      console.log('🧑 Session user:', session?.user);
      
/* Not using currently
      await supabase.rpc('set_config', {
        key: 'request.jwt.claims.vrc',
        value: vrc,
        is_local: true
      });
*/

    const { data: debugData, error: debugErr } = await supabase.rpc('get_vrc_debug');
    console.log("🧪 VRC Debug Result:", debugData, debugErr);



    const { data, error } = await supabase
      .from('hosting')
      .select('*')
      .eq('vrc', vrc)
      .single(); 

      console.log('Fetched event object:', data, error);

      if (error || !data) {
        setStep('error');
      } else {
        setEvent(data);
        setStep('enter-passphrase');
      }
    };

    validateVRC();
  }, [vrc]);



  const handlePassphraseSubmit = async (e) => {
    e.preventDefault();
    const userInput = passphrase.trim().toLowerCase();

    // ✅ Try member path first
    if (user) {
      const { data: memberData, error: memberError } = await supabase
        .from('guests')
        .select('*')
        .eq('vrc', vrc)
        .eq('guest_id', user.id)
        .limit(1);

      if (memberError) {
        console.error("🔴 Member check error:", memberError);
        setError("Something went wrong. Please try again.");
        return;
      }

      if (memberData && memberData.length > 0) {
        console.log("✅ Member access granted via guest_id");
        setStep('verified');
        return;
      }
    }

    // 🔑 Fallback: passphrase route
    const { data, error } = await supabase
      .from('guests')
      .select('*')
      .eq('vrc', vrc)
      .ilike('passphrase', userInput)
      .limit(1);

    if (error) {
      console.error("🔴 Passphrase check error:", error);
      setError("Something went wrong. Please try again.");
      return;
    }

    if (data && data.length > 0) {
      console.log("✅ Guest access granted via passphrase");
      setStep('verified');
    } else {
      setError('Incorrect passphrase. Please try again.');
    }
  };



const handleContactRequest = async () => {
  if (!contactEmail.includes('@')) {
    setContactFeedback('Please enter a valid email address.');
    return;
  }

  console.log("📩 Contact request submitted:", {
    email: contactEmail,
    vrc,
    eventName: event?.event_name || '[unknown event]',
  });

  // Optional: Save to Supabase or trigger email logic here

  setContactFeedback('Thanks! The event host will be notified.');
  setContactEmail('');

  setTimeout(() => setContactFeedback(''), 5000);
};


  if (step === 'loading') return <div className="speakeasy-loading">Validating VRC...</div>;

  if (step === 'error') {

    return (
      <div className="speakeasy-error-screen" style={{ textAlign: 'center' }}>
        <div>
          Wrong virtual room code my friend!{' '}
          <MeltingFaceIcon
            size={16}
            color="currentColor"
            style={{ verticalAlign: 'middle', marginLeft: '4px' }}
          />
        </div>

        <button
          style={{ marginTop: '12px' }}
          onClick={() => navigate('/')}
        >
          Back to Home
        </button>
      </div>
    );
  }

  const handleContactSubmit = async () => {
  const newErrors = {
    name: !contactName.trim(),
    email: !contactInfo.includes('@'),
    message: !contactMessage.trim()
  };

  setErrors(newErrors);

  if (Object.values(newErrors).some((e) => e)) {
    return;
  }

  const { error } = await supabase.from('join_requests').insert([
    {
      name: contactName.trim(),
      email: contactInfo.trim(),
      message: contactMessage.trim(),
      vrc,
      event_id: event?.id || null,
      type: 'contact'
    }
  ]);

  if (error) {
    console.error('❌ Failed to submit contact message:', error.message);
    return;
  }

  // Reset form and close modal
  setContactName('');
  setContactInfo('');
  setContactPhone('');
  setContactMessage('');
  setContactSocials('');
  setShowContactModal(false);
};

  return (
    <div className="speakeasy-container">
      {step === 'enter-passphrase' && (
        <div className="speakeasy-modal-overlay">
          <div className="speakeasy-modal">
            <h3>What's the passphrase for this event?</h3>
            <form onSubmit={handlePassphraseSubmit}>
              <input
                type="password"
                placeholder="Enter passphrase"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                className="speakeasy-input"
              />
              <button type="submit" className="speakeasy-button">Enter Event</button>
            </form>
                <div className="speakeasy-contact-help">
                  <p>Can’t remember the passphrase?</p>
                  <input
                    type="email"
                    placeholder="Enter your email to request access"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="speakeasy-input"
                  />
                  <button
                    onClick={handleContactRequest}
                    className="speakeasy-button secondary"
                    style={{ marginTop: '8px' }}
                  >
                    Request Access
                  </button>
                  {contactFeedback && <div className="speakeasy-feedback">{contactFeedback}</div>}
                </div>


            {error && <div className="speakeasy-error">{error}</div>}
          </div>
        </div>
      )}

        {step === 'verified' && event && (
          <>
            <EventViewer
              event={event}
              externalUser={user}
            />
          </>
        )}


      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '500px', width: '90%' }}
          >
            <h3 style={{ marginBottom: '1rem' }}>Send a Message to the Moderator</h3>

            <input
              type="text"
              placeholder="Name *"
              value={contactName}
              onChange={(e) => {
                setContactName(e.target.value);
                setErrors((prev) => ({ ...prev, name: false }));
              }}
              style={{
                width: '100%',
                marginBottom: '10px',
                borderColor: errors.name ? 'red' : '#ccc',
              }}
            />

            <input
              type="email"
              placeholder="Email *"
              value={contactInfo}
              onChange={(e) => {
                setContactInfo(e.target.value);
                setErrors((prev) => ({ ...prev, email: false }));
              }}
              style={{
                width: '100%',
                marginBottom: '10px',
                borderColor: errors.email ? 'red' : '#ccc',
              }}
            />

            <input
              type="text"
              placeholder="Phone Number (optional)"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              style={{ width: '100%', marginBottom: '10px' }}
            />

            <input
              type="text"
              placeholder="Optional social links (Instagram, etc.)"
              value={contactSocials}
              onChange={(e) => setContactSocials(e.target.value)}
              style={{ width: '100%', marginBottom: '10px' }}
            />

            <textarea
              placeholder="Your message *"
              value={contactMessage}
              onChange={(e) => {
                setContactMessage(e.target.value);
                setErrors((prev) => ({ ...prev, message: false }));
              }}
              rows={4}
              style={{
                width: '100%',
                marginBottom: '10px',
                borderColor: errors.message ? 'red' : '#ccc',
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span
                className="modal-icon-button"
                onClick={() => setShowContactModal(false)}
                title="Cancel"
                role="button"
                tabIndex={0}
              >
                <CloseIcon size={24} />
              </span>

              <span
                className="modal-icon-button"
                onClick={handleContactSubmit}
                title="Send"
                role="button"
                tabIndex={0}
              >
                <SendInviteIcon size={24} />
              </span>

            </div>
          </div>
        </div>
      )}

    </div>

  );
}
